// キャンバスをラスタ化する経路（PDF / 全体像スナップショット / サムネイル）で共有する画像読み込み。
//
// PDF 出力（canvas-pdf-export.ts）が持っていた実装をそのまま移し、サムネイル（canvas-thumbnail.ts）
// からも同じ経路で画像を読めるようにする。読み込み条件（crossOrigin / タイムアウト / blob: の除外・
// アバターの許可判定）を 2 か所へ複製すると、片方だけ直したときに描ける画像の範囲が食い違うため。

import type { CanvasObject } from "@refinement-canvas/domain";
import { isSnapshotAvatarImageUrl } from "../auth/avatar-image-url";

/** 画像 1 枚あたりの読み込み上限（ms）。超えた画像は解決せず、呼び出し側でプレースホルダへ落ちる。 */
export const IMAGE_LOAD_TIMEOUT_MS = 4_000;

/**
 * 未公開のローカル専用画像（このブラウザ内でだけ解決できる object URL）かどうか。
 *
 * スキームの比較は必ず `URL` で正規化してから行う。`src.startsWith("blob:")` のような
 * 文字列比較だと、`BLOB:...` のような大文字表記や前後の空白を含む表記が判定をすり抜ける一方、
 * ブラウザと `og-image-source.ts` の `classifyImageUrl`（`URL.protocol` で判定）はそれらを
 * 同じ `blob:` として扱うため、**外部配信の除外だけがすり抜ける**食い違いが生まれる。
 * 除外側と描画側でこの関数を共有し、判定規則を 1 か所へ固定する。
 */
export function isUnpublishedObjectUrl(src: string): boolean {
  try {
    return new URL(src).protocol === "blob:";
  } catch {
    // URL として解析できない文字列は object URL ではない（読み込みは別途失敗する）。
    return false;
  }
}

/**
 * リンクカード画像の読み込み元。
 * `src` を読み込み終えたら `release` を呼ぶ（発行された object URL を解放する）。
 * `data:` のように後始末が要らない場合、`release` は省略される。
 */
export interface LinkCardImageSource {
  readonly src: string;
  readonly release?: (() => void) | undefined;
}

/**
 * リンクカードの imageUrl を、実際に読み込める URL へ解決する関数。
 * 相手サイトの絶対 URL をそのまま読むと Cross-Origin-Resource-Policy / CORS で失敗するため、
 * 呼び出し元（React 側）が同一オリジンの中継経由で取得した object URL を返す。
 */
export type LinkCardImageResolver = (
  imageUrl: string
) => Promise<LinkCardImageSource | undefined>;

/** preloadImages の任意オプション。 */
export interface PreloadImagesOptions {
  /**
   * 進行中の読み込みを打ち切るシグナル。省略時は打ち切らない（移動前と同じ挙動）。
   * 中断は best-effort で、既に開始した取得は img の src 属性を外して打ち切りを促すだけである。
   */
  readonly signal?: AbortSignal | undefined;
  /**
   * 画像オブジェクトの placeholderSrc（永続化された LQIP）も読み込み対象へ含めるか。
   * 本体画像を読めなかったときに画面表示（image-object.tsx）と同じ絵へフォールバックしたい
   * サムネイルだけが true を渡す。PDF 出力は本体画像だけを描くため既定の false のままにする。
   */
  readonly includePlaceholderSrc?: boolean | undefined;
  /**
   * リンクカードの imageUrl を、読み込み可能な URL へ解決する。
   * 省略時、または undefined を返した場合は、その画像を読み込まない
   * （相手サイトの URL を直接読んで失敗し、コンソールへエラーを残すことを避ける）。
   */
  readonly resolveLinkCardImageUrl?: LinkCardImageResolver | undefined;
}

export async function preloadImages(
  objects: readonly CanvasObject[],
  excludeUnpublishedSrc: boolean,
  options?: PreloadImagesOptions
): Promise<Map<string, HTMLImageElement>> {
  const signal = options?.signal;
  const resolveLinkCardImageUrl = options?.resolveLinkCardImageUrl;
  const srcs = new Set<string>();
  // リンクカードの画像は解決関数を通してから読み込むため、他の src と分けて集める。
  const linkCardImageUrls = new Set<string>();
  // 未確定のローカル専用画像（blob: URL ＝ createAttachmentPlaceholder の本人用上書き等。サーバ未公開で
  // 他参加者にも配信されない）は、外部配信用スナップショットでは読み込まずプレースホルダへフォールバック
  // させる（drawObject 側で imageBySrc 未ヒット → プレースホルダ）。公開 URL へ finalize 済みなら通常どおり。
  // image.src だけでなく link-card.imageUrl も対象にする（attachment.update / クリップボード経由で blob: が
  // 入り得るうえ、スキーマ上 blob: は許可されているため）。
  const skipUnpublished = (src: string): boolean =>
    excludeUnpublishedSrc && isUnpublishedObjectUrl(src);
  for (const obj of objects) {
    if (obj.type === "image" && obj.src && !skipUnpublished(obj.src)) srcs.add(obj.src);
    // LQIP は data: URL で、オリジンの概念が無いため blob: 除外の対象にはならない。
    if (options?.includePlaceholderSrc && obj.type === "image" && obj.placeholderSrc) {
      srcs.add(obj.placeholderSrc);
    }
    if (obj.type === "link-card" && obj.imageUrl && !skipUnpublished(obj.imageUrl)) {
      linkCardImageUrls.add(obj.imageUrl);
    }
    // アバタースタンプは実画像をエクスポートでも描画し、キャンバス本体と見た目を一致させる。
    // ただし描画側（stamp-object）と同じく「安全なラスタ data:image/ スナップショット」のみ読み込み、
    // それ以外（リモート URL・実行可能 data: 等）は読み込まず 👤 フォールバックさせる
    // （外部 URL 取得によるトラッキング/CORS 汚染の防止と、描画境界の判定一致）。avatarSrc は
    // isSnapshotAvatarImageUrl で安全な data:image/ のみ許可するため blob: は元から除外される。
    if (
      obj.type === "stamp" &&
      obj.stampKind === "avatar" &&
      obj.avatarSrc &&
      isSnapshotAvatarImageUrl(obj.avatarSrc)
    ) {
      srcs.add(obj.avatarSrc);
    }
  }
  const entries = await Promise.all([
    ...Array.from(srcs).map(async (src) => {
      const img = await loadImage(src, signal);
      return img ? ([src, img] as const) : null;
    }),
    ...Array.from(linkCardImageUrls).map(async (imageUrl) => {
      // 解決関数が無い / 解決できない画像は読み込まない（直接読み込んで失敗させない）。
      // 解決そのものが失敗しても、1 枚のために描画全体を落とさない（他の画像と同じ best-effort にそろえる）。
      const resolved = await resolveLinkCardImageUrl?.(imageUrl).catch(() => undefined);
      if (resolved === undefined) return null;
      try {
        const img = await loadImage(resolved.src, signal);
        // 鍵は元の imageUrl のままにする（描画側が object.imageUrl で引くため）。
        return img ? ([imageUrl, img] as const) : null;
      } finally {
        // 読み込みが終わった（成功・失敗・中断のいずれでも）時点で解放する。
        // 読み込み済みの HTMLImageElement は、元の object URL を解放しても描画に使える。
        resolved.release?.();
      }
    }),
  ]);
  const map = new Map<string, HTMLImageElement>();
  for (const entry of entries) {
    if (entry) map.set(entry[0], entry[1]);
  }
  return map;
}

export function loadImage(src: string, signal?: AbortSignal): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    // CORS の絡みで toDataURL がブロックされるリスクを下げるため crossOrigin を付ける。
    // 同一オリジン or 公開バケットからの取得を想定する。失敗時はプレースホルダにフォールバックする。
    img.crossOrigin = "anonymous";
    let settled = false;
    // タイマー・リスナーを 1 か所で後始末し、中断とタイムアウトと完了が競合しても二重解決しないようにする。
    const finish = (value: HTMLImageElement | null): void => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      resolve(value);
    };
    const onAbort = (): void => {
      // src 属性を外すとブラウザは進行中の取得を打ち切る（打ち切れない環境でも finish で待たない）。
      // 空文字列の代入は避ける——古い実装では空文字を相対 URL として現在ページへ解決し、
      // 打ち切るどころか余分な GET を起こす。
      img.removeAttribute("src");
      finish(null);
    };
    const timer = window.setTimeout(() => finish(null), IMAGE_LOAD_TIMEOUT_MS);
    img.onload = () => finish(img);
    img.onerror = () => finish(null);
    signal?.addEventListener("abort", onAbort);
    // 既に中断済みなら取得を始めない（abort イベントはもう飛ばないため、ここで自分で閉じる）。
    if (signal?.aborted) {
      finish(null);
      return;
    }
    img.src = src;
  });
}
